import type { ResearchSelection } from "@sins2-atlas/compute";
import { isSubjectActive } from "@sins2-atlas/compute";
import type { ArtAsset, ExoticPrice, Unit, UnitItem } from "@sins2-atlas/game-data";
import { dataset, itemsById } from "./dataset";
import type { StatFrame } from "./frame";
import { researchSelectionOf } from "./frame-fold";
import {
  buildLoadoutIndex,
  EMPTY_LOADOUT,
  equip,
  type IndexedItem,
  itemLevelUnder,
  itemSlotsUnder,
  type Loadout,
  type LoadoutChange,
  type LoadoutIndex,
  type LoadoutRemoval,
  loadoutClosure,
  researchFor,
  slotsUsed,
  techSetWithItemResearch,
  unequip,
  withResearch,
} from "./loadout";
import { researchIndex, researchSubjectName } from "./tech-index";
import { EMPTY_TECH_SET } from "./tech-set";
import { exclusiveFactionOf } from "./vocabulary";

// The dataset's side of the Loadout (CONTEXT.md: Loadout; ADR 0012), exactly as
// tech-index.ts is the dataset's side of the Tech Set. lib/loadout.ts holds the rules — the
// cap, the ladders, the exclusions and the coupling to research — over an abstract index and
// imports no dataset; this module builds that index out of the shipped items, derives the
// rows the picker draws, and turns one tick into the next stat frame. Nothing here restates
// a rule: every transition below goes through equip / unequip / withResearch / loadoutClosure,
// and the slot arithmetic is only ever slotsUsed and itemSlotsUnder.

/** The 192 buyable ship items as the rules' index. Every id an item names in `neededItems`
 * or `mutuallyExclusiveItems` resolves here: extraction drops a reference it cannot resolve
 * and its own tests pin that, so a rung can never stand on nothing. */
export const loadoutIndex: LoadoutIndex = buildLoadoutIndex(
  dataset.items.map(
    (item): IndexedItem => ({
      id: item.id,
      needs: item.neededItems ?? [],
      excludes: item.mutuallyExclusiveItems ?? [],
      research: item.buildPrerequisites ?? [],
    }),
  ),
);

/** The Loadout this hull actually carries: the URL's items narrowed to its OWN shop. A
 * Loadout belongs to one hull (ADR 0012), so an id another hull buys is not an item this one
 * carries — the page already says so through unhonouredLoadout, and the slot arithmetic must
 * not count it either. Every transition below starts here, which is also why the first tick
 * on a hull clears such an id out of `?items=`: the reader has now stated what this hull
 * carries, and the notice has already been read. */
export function carriedLoadout(unit: Unit, frame: StatFrame): Loadout {
  const loadout = frame.loadout;
  if (loadout === undefined || loadout.size === 0) return EMPTY_LOADOUT;
  const own = new Set(unit.items);
  return new Set([...loadout].filter((id) => own.has(id)));
}

/** How many Item Slots this hull offers under this frame's research. The count is a research
 * fact, never a level one (ADR 0012): exactly one hull of 39 moves, and it moves on one
 * subject. */
export function itemSlotCapacity(unit: Unit, frame: StatFrame): number {
  return itemSlotsUnder(unit.itemSlots, researchSelectionOf(frame, unit.race));
}

/** Whether this hull can carry ship items at all. The control appears on exactly these 39
 * hulls and is absent everywhere else, rather than present and empty. */
export function hasItemSlots(unit: Unit): boolean {
  return unit.items.length > 0 && unit.itemSlots.some((rung) => rung.count > 0);
}

// --- The rows -----------------------------------------------------------------------

/** One tickable row: a ship item this hull can buy, under the game's own name and carrying
 * what the game's own shop tooltip carries for it — the hud icon, the Effect Lines of the
 * level in force, and the Price. Every item the hull can buy gets one, not only the 48% that
 * move a published stat: offering only those is the rule the ADR 0009 amendment already
 * rejected for research, on the grounds that a control which works on one row and refuses the
 * one beside it teaches the reader that the site is unreliable. */
export interface LoadoutOption {
  id: string;
  /** The game's own display name. */
  label: string;
  art: ArtAsset;
  /** The game-worded Effect Lines of the level in force. Empty is honest absence: 98 of the
   * 192 items compose no stat line at all (an ability, an engine flag, a description), and
   * the row says nothing rather than inventing one. */
  effects: readonly string[];
  /** The level in force and how many the item has, on a levelled item only (15 of 192). The
   * level is not a choice — the game levels items by research alone (ADR 0012) — so the row
   * states it rather than offering it. */
  level?: { current: number; count: number };
  price: UnitItem["price"];
  exoticPrice: readonly ExoticPrice[];
  /** The short name of the one Faction of the Race that can buy it, where the hull's Race has
   * another Faction to exclude. */
  faction?: string;
  /** The rung below it, by name (the game's `needed_items`). */
  needs: readonly string[];
  /** What it cannot be equipped beside, by name (the game's `mutually_exclusive_items`). */
  excludes: readonly string[];
  /** The Research Subjects that buy it, by their tree names. Ticking the row ticks them. */
  research: readonly string[];
}

/** One shop tab as the game groups it, in the hull's own listing order — the same grouping
 * the entity page's Items section draws, so a reader finds an item in the picker where the
 * game taught them to look. */
export interface LoadoutGroup {
  id: string;
  name: string;
  options: LoadoutOption[];
}

function itemNames(ids: readonly string[]): string[] {
  return ids.map((id) => itemsById.get(id)?.name ?? id);
}

function subjectNames(ids: readonly string[]): string[] {
  return ids.map((id) => researchSubjectName(id) ?? id);
}

function option(item: UnitItem, unit: Unit, research: ResearchSelection): LoadoutOption {
  const level = itemLevelUnder(item.levels, research);
  const faction = exclusiveFactionOf(unit.race, item.factions);
  return {
    id: item.id,
    label: item.name,
    art: item.hudIcon,
    effects: item.levels[level - 1]?.effects ?? [],
    ...(item.levels.length > 1 ? { level: { current: level, count: item.levels.length } } : {}),
    price: item.price,
    exoticPrice: item.exoticPrice,
    ...(faction === undefined || unit.factions.length < 2 ? {} : { faction: faction.short }),
    needs: itemNames(item.neededItems ?? []),
    excludes: itemNames(item.mutuallyExclusiveItems ?? []),
    research: subjectNames(item.buildPrerequisites ?? []),
  };
}

/** The hull's whole shop as the picker draws it: its own `items` listing, grouped by the
 * game's own tab in the order the listing first names each tab. */
export function loadoutGroupsFor(unit: Unit, frame: StatFrame): LoadoutGroup[] {
  const research = researchSelectionOf(frame, unit.race);
  const groups: LoadoutGroup[] = [];
  for (const id of unit.items) {
    const item = itemsById.get(id);
    if (item === undefined) continue;
    const found = groups.find((group) => group.id === item.buildGroup.id);
    const row = option(item, unit, research);
    if (found === undefined) {
      groups.push({ id: item.buildGroup.id, name: item.buildGroup.name, options: [row] });
    } else found.options.push(row);
  }
  return groups;
}

// --- The transitions ----------------------------------------------------------------

/** What one change did, in the site's own words, or undefined when it did exactly what the
 * reader asked and nothing else. Never silent: a Loadout that quietly loses an item the
 * reader chose, or quietly refuses a tick, is a Loadout they cannot trust. */
export interface LoadoutTransition {
  frame: StatFrame;
  note?: string;
}

const list = (parts: readonly string[]): string =>
  parts.length < 2 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;

const nameOf = (id: string): string => itemsById.get(id)?.name ?? id;

function removalNote(removal: LoadoutRemoval): string {
  const name = nameOf(removal.id);
  if (removal.reason === "excluded") {
    return `${name} came off: the game does not allow it beside ${nameOf(removal.because)}.`;
  }
  if (removal.reason === "requires-removed-item") {
    return `${name} came off: it needs ${nameOf(removal.because)}.`;
  }
  const subject = researchSubjectName(removal.because) ?? removal.because;
  return `${name} came off: the Tech Set no longer holds ${subject}.`;
}

/** The sentences one change produced, joined. The added rungs come first (they are what the
 * reader's one tick actually bought), then every removal in the order the change took it. */
function changeNotes(change: LoadoutChange, ticked?: string): string[] {
  const notes: string[] = [];
  if (change.added.length > 0) {
    // Bottom of the ladder first, the order the game would have bought them in: the ladder
    // arrives deepest-last from the closure, and a reader reads a chain upwards.
    const rungs = [...change.added].reverse().map(nameOf);
    const one = rungs.length === 1;
    const because =
      ticked === undefined ? "" : ` because ${nameOf(ticked)} needs ${one ? "it" : "them"}`;
    notes.push(`${list(rungs)} ${one ? "was" : "were"} equipped too,${because}.`);
  }
  for (const removal of change.removed) notes.push(removalNote(removal));
  return notes;
}

/** The frame carrying a new Loadout. Keeps the URL's unknown tokens (they are reported, not
 * resolved) and drops nothing else. */
function withLoadout(frame: StatFrame, loadout: Loadout): StatFrame {
  return { ...frame, loadout: loadout.size === 0 ? undefined : loadout };
}

/** Equip one item on this hull. The ladder below it comes too, whatever the game forbids
 * beside it comes off, the research that buys it joins the Tech Set, and every one of those
 * is said out loud. A tick the cap refuses changes nothing at all and says why. */
export function equipOnUnit(unit: Unit, frame: StatFrame, id: string): LoadoutTransition {
  const capacity = itemSlotCapacity(unit, frame);
  const carried = carriedLoadout(unit, frame);
  const change = equip(carried, id, loadoutIndex, capacity);
  if (change.refused === "no-room") {
    return {
      frame,
      note: `No room for ${nameOf(id)} on the ${unit.name}: it and what it needs would go past the ${capacity} item ${capacity === 1 ? "slot" : "slots"}, and ${slotsUsed(carried)} ${slotsUsed(carried) === 1 ? "is" : "are"} filled. Take something off first.`,
    };
  }
  if (change.refused === "forbidden") {
    return {
      frame,
      note: `${nameOf(id)} cannot be equipped: it needs items the game does not allow beside each other.`,
    };
  }
  const notes = changeNotes(change, id);
  const equipped = withLoadout(frame, change.loadout);
  const next = tickItemResearch(unit, equipped, [...change.loadout], notes);
  return { frame: next, note: join(notes) };
}

/** Take one item off. Every rung that stood on it goes too, and the note names it. */
export function unequipOnUnit(unit: Unit, frame: StatFrame, id: string): LoadoutTransition {
  const change = unequip(carriedLoadout(unit, frame), id, loadoutIndex);
  return { frame: withLoadout(frame, change.loadout), note: join(changeNotes(change)) };
}

/** Take everything off. The panel's empty extent, the Loadout's answer to Deselect all. It
 * touches no other axis: the research a previous tick pulled in stays, exactly as Deselect
 * all leaves the Corruption alone (ADR 0007). */
export function unequipAllOnUnit(frame: StatFrame): LoadoutTransition {
  return { frame: withLoadout(frame, EMPTY_LOADOUT) };
}

/** The research half of the coupling, forward (ADR 0012): equipping an item the game gates
 * behind research puts that research in the Tech Set, through the research closure so the
 * chain and the Tiers arrive with it. Fully-Teched already holds everything, so it is left
 * alone: it is the full extent, and there is no all-but-one to write. */
function tickItemResearch(
  unit: Unit,
  frame: StatFrame,
  ids: readonly string[],
  notes: string[],
): StatFrame {
  if (frame.tech === undefined && frame.research === "fullyTeched") return frame;
  const before = frame.tech ?? EMPTY_TECH_SET;
  const research = researchSelectionOf(frame, unit.race);
  const wanted = researchFor(ids, loadoutIndex).filter(
    (subject) => !isSubjectActive(subject, research),
  );
  if (wanted.length === 0) return frame;
  const after = techSetWithItemResearch(before, ids, loadoutIndex, researchIndex);
  if (after.size === before.size) return frame;
  notes.push(
    `${list(subjectNames(wanted))} joined the Tech Set, because the game sells these items only with that research.`,
  );
  return { ...frame, research: "base", tech: after };
}

/** The research half of the coupling, backwards (ADR 0012): the reader changed the research
 * axis, so every equipped item the Tech Set no longer buys comes off and the note names it.
 * The cap follows research too — one hull of 39 goes 6 slots to 8 on a subject — so a Loadout
 * that no longer fits is refitted through the same closure a `?items=` link goes through,
 * rather than left in a state the game could not produce.
 *
 * Every control on the entity page routes its new frame through here — through the hull's own
 * reconcile (lib/frame-reconcile.ts), which runs this first and then trims the allocation
 * against the Level — which is what makes the axes agree however the reader reaches them. */
export function reconcileLoadout(unit: Unit, next: StatFrame): LoadoutTransition {
  const carried = carriedLoadout(unit, next);
  if (carried.size === 0) return { frame: next };
  const research = researchSelectionOf(next, unit.race);
  const change = withResearch(carried, loadoutIndex, research);
  const notes = changeNotes(change);
  let loadout = change.loadout;
  const capacity = itemSlotsUnder(unit.itemSlots, research);
  const fits = slotsUsed(loadout) <= capacity;
  // Nothing came off, so the frame is left exactly as the control wrote it — including any
  // `?items=` id this hull cannot buy, which the page is still reporting and which only a
  // deliberate tick on this hull clears.
  if (change.removed.length === 0 && fits) return { frame: next };
  if (!fits) {
    const refit = loadoutClosure([...loadout], loadoutIndex, capacity);
    const dropped = [...loadout].filter((id) => !refit.loadout.has(id));
    loadout = refit.loadout;
    notes.push(
      `${list(itemNames(dropped))} came off: this hull has ${capacity} item ${capacity === 1 ? "slot" : "slots"} without that research.`,
    );
  }
  return { frame: withLoadout(next, loadout), note: join(notes) };
}

/** What a `?items=` link settles to: the Loadout the same ticks would have produced.
 *
 * Every other transition in this module is reached by a CLICK, and each click goes through
 * a door — equipOnUnit pulls in the ladder below an item, drops whatever the game forbids
 * beside it, stops at the hull's slots and ticks in the research that buys it. A link goes
 * through none of them, so a cold `?items=` could render a Loadout no player can field: the
 * Anathema cloaking at Perfect (5) with Cloak Generator Design unticked, or an Argonev
 * carrying Auxiliary Hangar III with neither rung beneath it. Both were reachable before
 * this function existed (found reviewing the Cloak round, 2026-09-07; the defect is issue
 * 45's and is shared by every gated item, Reactive Plating included).
 *
 * So the link is replayed as the clicks it stands for, in the URL's own order, from an empty
 * hull — building UP is what makes the hull's slots bind the way they bind a reader, rather
 * than a full-and-overfull Loadout refusing every tick. Ids belonging to some OTHER hull are
 * carried through untouched: they are reported, not resolved (unhonouredLoadout), and the
 * comparison view reads them off this very frame. A link the game could already produce is
 * returned unchanged, identity and all, so an ordinary page pays nothing for this. */
export function settleLoadout(unit: Unit, frame: StatFrame): LoadoutTransition {
  const carried = carriedLoadout(unit, frame);
  if (carried.size === 0) return { frame };
  const foreign = [...(frame.loadout ?? [])].filter((id) => !carried.has(id));
  const notes: string[] = [];
  let next = withLoadout(frame, new Set(foreign));
  for (const id of carried) {
    const step = equipOnUnit(unit, next, id);
    next = step.frame;
    if (step.note !== undefined) notes.push(step.note);
  }
  const settled = carriedLoadout(unit, next);
  const unchanged =
    settled.size === carried.size &&
    [...settled].every((id) => carried.has(id)) &&
    next.tech === frame.tech &&
    next.research === frame.research;
  if (unchanged) return { frame };
  return { frame: withLoadout(next, new Set([...settled, ...foreign])), note: join(notes) };
}

function join(notes: readonly string[]): string | undefined {
  return notes.length === 0 ? undefined : notes.join(" ");
}
